import { Physics } from '@react-three/rapier';
import { useRef, useEffect, Suspense } from 'react';
import Lights from './Lights.jsx';
import Level from './Level.jsx';
import Player from './Player.jsx';
import { useCameraFollow } from '../hooks/useCameraFollow.js';
import { useGameStore } from '../stores/gameStore.js';
// import { Perf } from 'r3f-perf';
import PlayerTrail from './PlayerCubeTrail.jsx';
import FloatingParticles from './FloatingParticles.jsx';
import CameraFadeMask from './CameraFadeMask.jsx';
import WhitePlane from './WhitePlane.jsx';
import ZoneTrigger from './triggers/ZoneTrigger.jsx';
// import DebugPosition from './DebugPosition.jsx';

export default function Experience() {
    const playerRef = useRef();
    const phase = useGameStore((state) => state.phase);

    useCameraFollow(playerRef);

    // Handle Launch Sequence (Transition from Launching -> Playing)
    useEffect(() => {
        if (phase === 'launching') {
            const timer = setTimeout(() => {
                useGameStore.getState().startGame(); // This plays music & sets phase to 'playing'
            }, 1500); // 1.5s to allow for the zoom & blackout animation
            return () => clearTimeout(timer);
        }
    }, [phase]);

    // Teleport player to the new "sector" when game starts (hidden by blackout)
    useEffect(() => {
        if (phase === 'playing' && playerRef.current) {
            playerRef.current.setTranslation({ x: 400, y: 1, z: 0 }, true);
            // Kickstart movement immediately to prevent "stuck" feeling
            playerRef.current.setLinvel({ x: 5.5, y: 0, z: 0 }, true);
            playerRef.current.setAngvel({ x: 0, y: 0, z: 0 }, true);
            playerRef.current.wakeUp();
            return;
        }

        // Reset player to start if resetting game
        if ((phase === 'ready' || phase === 'launching') && playerRef.current) {
            const t = playerRef.current.translation();
            // If we are far away (end level), teleport back
            if (t.x > 50) {
                playerRef.current.setTranslation({ x: 0, y: 1, z: 0 }, true);
                playerRef.current.setLinvel({ x: 0, y: 0, z: 0 }, true);
                playerRef.current.setAngvel({ x: 0, y: 0, z: 0 }, true);
                playerRef.current.setRotation({ x: 0, y: 0, z: 0, w: 1 }, true);
            }
        }
    }, [phase]);

    return (
        <>
            {/* <Perf /> */}
            <CameraFadeMask />
            <WhitePlane />
            {/* <DebugPosition playerRef={playerRef} /> */}
            <FloatingParticles />
            <Lights />
            <PlayerTrail playerRef={playerRef} />

            <Physics gravity={[0, 0, 0]} debug={false}>
                <Level playerRef={playerRef} />
                <Player ref={playerRef} />

                {/* Level Triggers System */}
                {levelTriggers.map((trigger, index) => (
                    <ZoneTrigger
                        key={index}
                        position={[trigger.x, 50, 0]} // La posició Y està al mig (50) perquè l'alçada sigui 100 començant de 0
                        size={[0.5, 100, 4]} // Alçada de 100
                        onEnter={trigger.action}
                        debug={false}
                    />
                ))}
            </Physics>
        </>
    );
}

// Seqüència de 32 compassos rítmics des de X=493 fins a X=563 aprox
const rhythmicTriggers = Array.from({ length: 32 }).map((_, i) => {
    const triggerX = 493 + (i * 2.25);
    return {
        x: triggerX,
        action: () => {
            const store = useGameStore.getState();
            const spawnDist = 15; // Apropat perquè tinguis menys marge d'avís i l'impacte visual sigui més fort

            // RENDIMENT: Canviem la pluja d'objectes massiva per UN SOL CAMÍ net i elegant.
            // GAMEFEEL: Creem una hèlix (una espiral 3D) per on el jugador haurà de volar.
            // Visualment dona sensació de velocitat, fosc i ciberpunk net (molt "Sènior").

            const angle = i * 0.5; // Espaiament angular de l'espiral
            const zPos = Math.sin(angle) * 3.5;
            const yPos = 3 + Math.cos(angle) * 2.5; // Centre Y=3, oscil·la de 0.5 a 5.5

            // Mida del beat
            const isBigBeat = i % 4 === 0;

            store.spawnRhythmicObstacle({
                x: triggerX + spawnDist,
                y: yPos,
                z: zPos,
                type: isBigBeat ? 3 : (i % 3), // 3=Dodecaedre als Drops forts, altre la resta
                scale: isBigBeat ? 1.5 : 0.8
            });
        }
    };
});

// Configuració del Nivell - Offset +400 unitats (Després de la transició)
const levelTriggers = [
    { x: 418, action: () => { console.log('⚡ Fade Out Blackout'); useGameStore.getState().setBlackout(false); } }, // 17 + 400
    // L'usuari vol una experiència purament contemplativa, MAI morir, per tant Z fora del camí jugable (ex: Z = ±6)
    { x: 433, action: (playerPos) => useGameStore.getState().spawnTesseract({ x: 436 + 15 + (Math.random() * 5), y: playerPos ? playerPos.y + (Math.random() - 0.5) * 1.5 : 2, z: 0 }) },
    { x: 440, action: (playerPos) => useGameStore.getState().spawnTesseract({ x: 444 + 15 + (Math.random() * 5), y: playerPos ? playerPos.y + (Math.random() - 0.5) * 1.5 : 3, z: 0 }) },
    { x: 442, action: (playerPos) => useGameStore.getState().spawnTesseract({ x: 446 + 15 + (Math.random() * 5), y: playerPos ? playerPos.y + (Math.random() - 0.5) * 1.5 : 1, z: 0 }) },
    { x: 452, action: (playerPos) => useGameStore.getState().spawnTesseract({ x: 455 + 15 + (Math.random() * 5), y: playerPos ? playerPos.y + (Math.random() - 0.5) * 1.5 : 4, z: 0 }) },
    { x: 460, action: (playerPos) => useGameStore.getState().spawnTesseract({ x: 463 + 15 + (Math.random() * 5), y: playerPos ? playerPos.y + (Math.random() - 0.5) * 1.5 : 2, z: 0 }) },
    { x: 462, action: (playerPos) => useGameStore.getState().spawnTesseract({ x: 465 + 15 + (Math.random() * 5), y: playerPos ? playerPos.y + (Math.random() - 0.5) * 1.5 : 5, z: 0 }) },
    { x: 470, action: () => { console.log('📸 Camera Change -> Action View (Chase)'); useGameStore.getState().setCameraView('chase'); } }, // 72 + 400

    // RHYTHM SEQUENCE (x: 493 to x: 563) - 11 Beats synchronized with gameplay
    // COMPTE: Temporalment desactivats mentre re-pensem una alternativa visual que no generi lag
    // ...rhythmicTriggers,

    {
        x: 552, action: () => {
            console.log('Blackout, change camera angle');
            useGameStore.getState().setFinalBlackout(true);
        }
    }, // 160 + 400
    // { x: 568, action: () => console.log('🔥 Climax Sequence') }, // 165 + 400
    // { x: 639, action: () => console.log('🧘 Calm Section') }, // 239 + 400
    {
        x: 553, action: () => {
            console.log('🏁 End Level (Tape Stop & Orbit)');
            useGameStore.getState().triggerLevelEnd();
            useGameStore.getState().setPhase('ended');
        }
    }, // 313 + 400
];